import type { AxiosInstance } from "axios";

export type SchedulerMode = "automatic" | "manual";
export type ManagedResourceType =
    | "ecs-service"
    | "ec2-instance"
    | "eks-node-group"
    | "rds-instance"
    | "rds-cluster"
    | "auto-scaling-group";
export type DesiredState = "running" | "stopped";
export type OperationStatus = "queued" | "running" | "succeeded" | "partial" | "failed";

export interface AutomaticSchedule {
    daysOfWeek: string[];
    startTime: string;
    stopTime: string;
}

export interface LeaseConfiguration {
    defaultMinutes: number;
    maxMinutes: number;
    safetyStopTime: string;
}

export interface SchedulerState {
    desiredState: DesiredState;
    expiresAt?: string | null;
    updatedAt: string;
    source: string;
}

export interface OperationResourceResult {
    resourceId: string;
    resourceType: ManagedResourceType;
    name: string;
    action: "start" | "stop";
    status: "succeeded" | "failed" | "skipped";
    message?: string;
}

export interface SchedulerOperation {
    id: string;
    action: "start" | "stop";
    source: "manual" | "schedule" | "safety-stop" | "reconciliation" | "configuration";
    status: OperationStatus;
    requestedAt: string;
    startedAt?: string;
    completedAt?: string;
    requestedBy?: string;
    error?: string;
    results?: OperationResourceResult[];
}

export interface Scheduler {
    environmentId: string;
    name: string;
    awsAccountId: string;
    awsRegion: string;
    mode: SchedulerMode;
    timezone: string;
    automaticSchedule: AutomaticSchedule;
    lease: LeaseConfiguration;
    managedResourceTypes: ManagedResourceType[];
    excludedResourceIds: string[];
    state: SchedulerState;
    latestOperation?: SchedulerOperation;
    createdAt: string;
    updatedAt: string;
}

export interface UpsertSchedulerRequest {
    name: string;
    awsAccountId: string;
    awsRegion: string;
    mode: SchedulerMode;
    timezone: string;
    automaticSchedule: AutomaticSchedule;
    lease: LeaseConfiguration;
    managedResourceTypes: ManagedResourceType[];
    excludedResourceIds: string[];
}

export type ResourceState =
    | "running"
    | "stopped"
    | "starting"
    | "stopping"
    | "transitioning"
    | "failed"
    | "unknown";

export interface SchedulerResource {
    id: string;
    arn?: string;
    name: string;
    type: ManagedResourceType;
    service: "EC2" | "ECS" | "EKS" | "Fargate" | "RDS" | "Auto Scaling";
    state: ResourceState;
    nativeState: string;
    desiredState: DesiredState;
    managed: boolean;
    eligible: boolean;
    eligibilityReason?: string | null;
    lastOperationId?: string | null;
    lastError?: string | null;
    details: Record<string, string | number | boolean | null | string[]>;
}

export interface ResourceSummary {
    total: number;
    managed: number;
    running: number;
    stopped: number;
    transitioning: number;
    failed: number;
}

export interface SchedulerResourcesResponse {
    scheduler: Scheduler;
    resources: SchedulerResource[];
    summary: ResourceSummary;
    warnings?: string[];
    refreshedAt: string;
}

export type CostExplorerPeriodPreset = "month-to-date" | "last-month";
export type CostExplorerGroupBy = "service" | "environment" | "account" | "region";

export interface CostExplorerFilters {
    accountIds: string[];
    period: CostExplorerPeriodPreset;
    groupBy: CostExplorerGroupBy;
    environmentId?: string;
    region?: string;
    service?: string;
}

export interface CostExplorerPeriod {
    preset: CostExplorerPeriodPreset;
    start: string;
    end: string;
    comparisonStart: string;
    comparisonEnd: string;
    dataThrough: string;
    estimated: boolean;
}

export interface CostExplorerSummary {
    currentCost: number;
    projectedMonthEnd: number | null;
    dailyAverage: number;
    comparisonCost: number;
    changeAmount: number;
    changePercent: number | null;
    matchedCost: number;
    unmatchedCost: number;
    matchedPercent: number;
}

export interface CostExplorerBreakdownItem {
    id: string;
    name: string;
    cost: number;
    share: number;
    comparisonCost: number;
    changeAmount: number;
    changePercent: number | null;
    children: CostExplorerBreakdownChild[];
}

export interface CostExplorerBreakdownChild {
    id: string;
    name: string;
    cost: number;
    shareOfParent: number;
    comparisonCost: number;
    changeAmount: number;
    changePercent: number | null;
}

export interface CostExplorerTimeSeriesGroup {
    id: string;
    name: string;
    cost: number;
}

export interface CostExplorerTimeSeriesPoint {
    date: string;
    total: number;
    estimated: boolean;
    groups: CostExplorerTimeSeriesGroup[];
}

export interface CostExplorerInsight {
    id: string;
    tone: "default" | "warning" | "positive" | string;
    title: string;
    body: string;
    groupId?: string;
}

export interface CostExplorerWarning {
    accountId: string;
    message: string;
}

export interface CostExplorerResponse {
    period: CostExplorerPeriod;
    currency: string;
    summary: CostExplorerSummary;
    breakdown: CostExplorerBreakdownItem[];
    timeSeries: CostExplorerTimeSeriesPoint[];
    insights: CostExplorerInsight[];
    serviceOptions: string[];
    warnings: CostExplorerWarning[];
    generatedAt: string;
}

export class CostOptimizerApi {
    constructor(
        private readonly basePath: string,
        private readonly axios: AxiosInstance,
        private readonly accessToken: () => string | undefined
    ) {}

    private headers(projectId: string) {
        const token = this.accessToken();
        return {
            projectId,
            ...(token ? { Authorization: `Bearer ${token}` } : {})
        };
    }

    async getCostExplorer(projectId: string, filters: CostExplorerFilters) {
        return this.axios.get<CostExplorerResponse>(`${this.basePath}/costs/explorer`, {
            headers: this.headers(projectId),
            params: {
                accountIds: filters.accountIds.join(","),
                period: filters.period,
                groupBy: filters.groupBy,
                ...(filters.environmentId ? { environmentId: filters.environmentId } : {}),
                ...(filters.region ? { region: filters.region } : {}),
                ...(filters.service ? { service: filters.service } : {})
            }
        });
    }

    async listSchedulers(projectId: string) {
        return this.axios.get<{ schedulers: Scheduler[] }>(`${this.basePath}/schedulers`, {
            headers: this.headers(projectId)
        });
    }

    async getScheduler(projectId: string, environmentId: string) {
        return this.axios.get<Scheduler>(`${this.basePath}/schedulers/${encodeURIComponent(environmentId)}`, {
            headers: this.headers(projectId)
        });
    }

    async upsertScheduler(projectId: string, environmentId: string, request: UpsertSchedulerRequest) {
        return this.axios.put<Scheduler>(
            `${this.basePath}/schedulers/${encodeURIComponent(environmentId)}`,
            request,
            { headers: this.headers(projectId) }
        );
    }

    async deleteScheduler(projectId: string, environmentId: string) {
        return this.axios.delete<void>(`${this.basePath}/schedulers/${encodeURIComponent(environmentId)}`, {
            headers: this.headers(projectId)
        });
    }

    async listResources(projectId: string, environmentId: string) {
        return this.axios.get<SchedulerResourcesResponse>(
            `${this.basePath}/schedulers/${encodeURIComponent(environmentId)}/resources`,
            { headers: this.headers(projectId) }
        );
    }

    async start(projectId: string, environmentId: string, leaseMinutes?: number) {
        return this.axios.post<SchedulerOperation>(
            `${this.basePath}/schedulers/${encodeURIComponent(environmentId)}/actions/start`,
            leaseMinutes ? { leaseMinutes } : {},
            { headers: this.headers(projectId) }
        );
    }

    async stop(projectId: string, environmentId: string) {
        return this.axios.post<SchedulerOperation>(
            `${this.basePath}/schedulers/${encodeURIComponent(environmentId)}/actions/stop`,
            {},
            { headers: this.headers(projectId) }
        );
    }

    async extendLease(projectId: string, environmentId: string, durationMinutes: number) {
        return this.axios.post<Scheduler>(
            `${this.basePath}/schedulers/${encodeURIComponent(environmentId)}/lease/extend`,
            { durationMinutes },
            { headers: this.headers(projectId) }
        );
    }

    async listOperations(projectId: string, environmentId: string) {
        return this.axios.get<{ operations: SchedulerOperation[] }>(
            `${this.basePath}/schedulers/${encodeURIComponent(environmentId)}/operations`,
            { headers: this.headers(projectId) }
        );
    }
}
